import { Layers } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * The frame shared by every screen a signed-out user can reach.
 *
 * Sign-in, forgot-password and reset-password are the only three, and they are the first
 * thing anyone sees — keeping the card, the heading and the spacing in one place is what
 * stops the reset screen from looking like a different product than the login screen.
 *
 * The mark is the product's, not the company's. A customer's uploaded logo lives behind
 * `company_settings`, which needs a session to read, so a signed-out screen cannot show it
 * without either leaking branding to anyone who loads the page or flashing a placeholder
 * and swapping it a moment later.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-100 p-6 dark:bg-slate-950">
      {/* Two soft washes of brand colour behind the card. Decorative, so it is hidden from
          the accessibility tree and cannot be tabbed into. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-emerald-500/15 blur-3xl dark:bg-emerald-500/10"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-cyan-500/15 blur-3xl dark:bg-cyan-500/10"
      />

      <div className="relative w-full max-w-sm space-y-6">
        <header className="space-y-3 text-center">
          <Link to="/" className="inline-flex items-center gap-3 transition-transform hover:scale-105">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-tr from-emerald-600 to-cyan-500 shadow-xl shadow-emerald-500/25">
              <Layers className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 leading-tight">
                FMCG <span className="text-emerald-600 dark:text-emerald-400">ERP Suite</span>
              </h1>
              <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30">
                AI Enterprise
              </span>
            </div>
          </Link>
          {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
        </header>

        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-900/5 dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/30">
          <h2 className="sr-only">{title}</h2>
          {children}
        </div>

        {footer}
      </div>
    </main>
  )
}
